import {
  SequenceMenuDivider,
  SequenceMenu,
  SequenceMenuItemProps,
  SequenceMenuItemType,
  Center
} from '@sequencehq/core-components'
import { ActiveFilter } from '@sequencehq/core-components'
import Invoices from 'components/Invoices'
import Quotes from 'modules/Quotes/Quotes'
import CreditNotes from 'modules/CreditNotesList/CreditNotes'
import BillingSchedules from 'modules/BillingSchedules/BillingSchedules'
import Customers from 'modules/Customers/Customers'
import { Dispatch, FC, SetStateAction, useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { DeleteSavedViewDialog } from 'SavedViews/components/DeleteSavedViewDialog'
import { useMutation, useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useNotifications } from '@sequencehq/core-components'
import { ErrorMessage } from 'components/ErrorPages/ErrorMessage'
import { Spinner } from '@sequencehq/core-components'
import { savedViewsPorts } from 'SavedViews/savedViews.ports'
import { SAVED_VIEWS_STALE_TIME } from 'SavedViews/savedViews.constants'
import { match } from 'ts-pattern'
import { logError } from '@sequencehq/utils'
import { useSearchParams } from 'react-router-dom'

export const SavedViewPage: FC = () => {
  const { id } = useParams<{ id: string }>()
  const { displayNotification } = useNotifications()

  const {
    data: savedViews,
    isLoading,
    isFetching
  } = useQuery(dashboard20240730Client.getSavedViews, undefined, {
    select: data => {
      if (!data) {
        return null
      }

      return savedViewsPorts.savedViews.fromServer(data)
    },
    staleTime: SAVED_VIEWS_STALE_TIME
  })
  const putSavedViewMutator = useMutation(dashboard20240730Client.putSavedView)

  const savedView = savedViews?.find(savedView => savedView.id === id)

  useSetSortBySearchParam(savedView?.sortBy ?? null)

  const kebabMenuItems = (
    setSavingFilters: Dispatch<SetStateAction<false | ActiveFilter[]>>
  ): SequenceMenuItemType[] | undefined => {
    if (!savedView) {
      return undefined
    }

    return [
      (props: Partial<SequenceMenuItemProps>) => (
        <SequenceMenu.Item
          key="edit-name"
          uuid="edit-name"
          label="Edit name"
          onClick={() => {
            setSavingFilters(savedView.activeFilters)
            props.setIsOpen?.(false)
          }}
        />
      ),
      <SequenceMenuDivider key="divider" />,
      (props: Partial<SequenceMenuItemProps>) => (
        <DeleteSavedViewDialog
          key="delete-saved-view"
          savedViewId={savedView.id}
          onCancel={() => props.setIsOpen?.(false)}
        />
      )
    ]
  }

  const onUpdateSavedFilters = useCallback(
    async (updatedActiveFilters: ActiveFilter[], sortBy: string | null) => {
      if (!savedView) {
        return
      }

      const hasFilterChanges =
        JSON.stringify(updatedActiveFilters) !==
        JSON.stringify(savedView.activeFilters)

      const hasSortByChanges = sortBy !== savedView.sortBy

      // Check if there are meaningful changes to the saved view to prevent unnecessary updates
      if (!hasFilterChanges && !hasSortByChanges) {
        return
      }

      const resPutSavedView = await putSavedViewMutator.mutateAsync({
        id: savedView.id,
        body: savedViewsPorts.savedView.toServer({
          name: savedView.name,
          entityType: savedView.entityType,
          activeFilters: updatedActiveFilters,
          sortBy
        })
      })

      if (!resPutSavedView) {
        displayNotification('Failed to update saved view', { type: 'error' })
        return
      }

      displayNotification('Saved view updated', { type: 'success' })
    },
    [savedView, putSavedViewMutator.mutateAsync, displayNotification]
  )

  if (isLoading || (!savedView && isFetching)) {
    return (
      <Center height="100%" width="100%">
        <Spinner />
      </Center>
    )
  }

  if (!savedView) {
    return <ErrorMessage errorType="notFound" />
  }

  return match(savedView.entityType)
    .with('QUOTES', () => (
      <Quotes
        key={id}
        title={savedView.name}
        forcedActiveFilters={savedView.activeFilters}
        kebabMenuItems={kebabMenuItems}
        onUpdateSavedFilters={onUpdateSavedFilters}
      />
    ))
    .with('CREDIT_NOTES', () => (
      <CreditNotes
        key={id}
        title={savedView.name}
        forcedActiveFilters={savedView.activeFilters}
        kebabMenuItems={kebabMenuItems}
        onUpdateSavedFilters={onUpdateSavedFilters}
      />
    ))
    .with('BILLING_SCHEDULES', () => (
      <BillingSchedules
        key={id}
        title={savedView.name}
        forcedActiveFilters={savedView.activeFilters}
        kebabMenuItems={kebabMenuItems}
        onUpdateSavedFilters={onUpdateSavedFilters}
      />
    ))
    .with('CUSTOMERS', () => (
      <Customers
        key={id}
        title={savedView.name}
        forcedActiveFilters={savedView.activeFilters}
        kebabMenuItems={kebabMenuItems}
        onUpdateSavedFilters={onUpdateSavedFilters}
      />
    ))
    .with('INVOICES', () => (
      <Invoices
        key={id}
        title={savedView.name}
        forcedActiveFilters={savedView.activeFilters}
        kebabMenuItems={kebabMenuItems}
        onUpdateSavedFilters={onUpdateSavedFilters}
      />
    ))
    .otherwise(() => {
      logError('Unknown saved view entity type', {
        savedViewEntityType: savedView.entityType
      })
      return null
    })
}

// Set the sortBy search param when the saved view is loaded
// but never override it if it already exists
const useSetSortBySearchParam = (sortBy: string | null) => {
  const [, setSearchParams] = useSearchParams(
    new URLSearchParams(window.location.search)
  )

  useEffect(() => {
    if (sortBy) {
      setSearchParams(prev => {
        if (!prev.has('sortBy')) {
          prev.set('sortBy', sortBy)
        }
        return prev
      })
    }
  }, [sortBy, setSearchParams])
}
